import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BookA, RefreshCw, Loader2, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const ETYMO_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/oyh7gal4_gemini_generated_video_999c2ed1.mp4";
import { CardActions } from "@/components/CardActions";

export function Etymology() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catIdx, setCatIdx] = useState(null);
  const [sharing, setSharing] = useState(false);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/etymology", { params: { refresh, lang: i18n.language } });
      setEntry(data.term ? { term: data.term, text: data.text } : null);
      setCatIdx(typeof data.category_index === "number" ? data.category_index : null);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.etymoError"));
      setEntry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [i18n.language]); // eslint-disable-line

  const shareCard = async () => {
    if (sharing || !entry) return;
    setSharing(true);
    try {
      const cat = catIdx !== null ? (t("atelier.etymoCats", { returnObjects: true }) || [])[catIdx] : "";
      const { data: blob } = await api.post("/etymology/card", {
        lang: (i18n.language || "fr").slice(0, 2),
        term: entry.term,
        text: entry.text,
        eyebrow: t("atelier.etymoCardLabel"),
        category: cat || "",
        cta: t("atelier.etymoShareCta"),
      }, { responseType: "blob" });
      const file = new File([blob], "cocoon-etymologie.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text: t("atelier.etymoShareText", { term: entry.term }) }); } catch { /* cancelled */ }
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = file.name; document.body.appendChild(a); a.click();
        a.remove(); URL.revokeObjectURL(u);
        toast.success(t("atelier.etymoShareReady"));
      }
    } catch { toast.error(t("atelier.etymoShareError")); }
    finally { setSharing(false); }
  };

  const openStory = () => {
    if (!entry) return;
    localStorage.setItem("coccon_pending_prompt", t("atelier.etymoStoryPrompt", { term: entry.term }));
    navigate("/atelier");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      data-testid="etymology"
      className="max-w-3xl mx-auto glass stats-wow rounded-2xl px-7 py-9 text-center"
    >
      <div className="flex flex-col items-center">
        <div className="max-w-xl w-full mx-auto mb-8">
          <CinematicVideo
            src={ETYMO_VIDEO}
            testId="etymo-cinematic-video"
            caption={t("atelier.etymoTitle")}
            fade
            noSound
          />
        </div>
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <BookA className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-2">{t("atelier.etymoTitle")}</p>
        {catIdx !== null && !loading && (
          <p className="text-[11px] text-foreground/40 mb-4" data-testid="etymo-cat">
            {t("atelier.factToday")} · <span className="text-primary/70">{(t("atelier.etymoCats", { returnObjects: true }) || [])[catIdx]}</span>
          </p>
        )}
        {loading && !entry ? (
          <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t("atelier.etymoLoading")}
          </p>
        ) : entry ? (
          <>
            <button
              type="button"
              onClick={openStory}
              data-testid="etymo-open-story"
              className="group block w-full text-center rounded-xl px-2 py-2 -my-2 transition-colors hover:bg-primary/[0.04] cursor-pointer"
              title={t("atelier.etymoStoryHint")}
            >
              <p className="font-display text-gradient-orange text-2xl sm:text-3xl mb-3 transition-transform duration-300 group-hover:scale-[1.02]" data-testid="etymo-term">
                {entry.term}
              </p>
              <p className="font-display italic text-base sm:text-lg text-foreground/85 leading-relaxed max-w-2xl mx-auto group-hover:text-foreground transition-colors" data-testid="etymo-text">
                {entry.text}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary/70 group-hover:text-primary transition-colors" data-testid="etymo-story-hint">
                <Sparkles size={13} /> {t("atelier.etymoStoryHint")}
              </span>
            </button>
          </>
        ) : (
          <p className="font-display italic text-foreground/40">{t("atelier.etymoError")}</p>
        )}
        <button
          onClick={() => load(true)} disabled={loading}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
          data-testid="etymo-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {t("atelier.etymoNew")}
        </button>
        {entry && (
          <button
            onClick={shareCard} disabled={sharing}
            className="mt-4 btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60"
            data-testid="etymo-share-card"
          >
            {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {t("atelier.etymoShare")}
          </button>
        )}
        {entry && <CardActions cardId="etymology" heading={`${entry.term} — ${entry.text}`} testId="etymo" />}
      </div>
    </motion.div>
  );
}
